// The deals screen's toolbar: the view toggle, the server-backed filters, the
// count of what those filters left, and the create action.
//
// Shared by the list and the board because the filters ARE the same query
// (`DealsQueryState`, in the URL) — a search typed above the table narrows the
// board too, and a person switching views expects to be looking at the same
// deals. Two toolbars would be two chances for a control to exist on one view
// and not the other.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Currency, DealState } from "@crm/shared";
import styles from "./DealsList.module.css";
import { Button } from "../../design/Button.js";
import { SearchInput } from "../../design/SearchInput.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { formatCount } from "../../format/money.js";
import { dealStateLabel } from "../../format/labels.js";
import type { DealsViewMode } from "./dealsSearch.js";
import {
  usePartnerOptions,
  type DealRenewalWindow,
  type DealStartWindow,
  type DealsQueryState,
} from "../../api/queries.js";

const ANY_CURRENCY = "__any__";
const ANY_PARTNER = "__any__";
const ANY_WIN = "__any__";

export const VIEW_OPTIONS: ReadonlyArray<SegmentedOption<DealsViewMode>> = [
  { value: "list", label: "List", title: "Every deal in a sortable table, a page at a time" },
  {
    value: "board",
    label: "Board",
    title: "A column per state, with what each column is worth — drag a deal to move it",
  },
  {
    value: "changes",
    label: "Changes",
    title: "How the pipeline moved over a period, and the deals that moved it",
  },
];

/** The floors on win probability worth a menu entry. */
const WIN_FLOORS: readonly number[] = [25, 50, 75, 100];

const START_WINDOWS: ReadonlyArray<{ value: DealStartWindow; label: string }> = [
  { value: "any", label: "Any start" },
  { value: "next_30", label: "Starting in 30 days" },
  { value: "next_90", label: "Starting in 90 days" },
  { value: "this_year", label: "Starting this year" },
  { value: "next_year", label: "Starting next year" },
];

const RENEWAL_WINDOWS: ReadonlyArray<{ value: DealRenewalWindow; label: string }> = [
  { value: "any", label: "Any renewal" },
  { value: "next_90", label: "Renewing in 90 days" },
  { value: "next_180", label: "Renewing in 6 months" },
  { value: "next_365", label: "Renewing in 12 months" },
];

/** Whether anything narrows the pipeline — what "Clear filters" would undo. */
export function dealsFiltersActive(state: DealsQueryState): boolean {
  return (
    state.q !== "" ||
    state.states.length > 0 ||
    state.partnerId !== null ||
    state.currency !== null ||
    state.winProbMin !== null ||
    state.start !== "any" ||
    state.renewal !== "any" ||
    state.country !== null ||
    state.location !== "any"
  );
}

export interface DealsToolbarProps {
  state: DealsQueryState;
  onStateChange: (next: DealsQueryState) => void;
  view: DealsViewMode;
  onViewChange: (next: DealsViewMode) => void;
  /** How many deals the filters matched; undefined until the query answers. */
  total: number | undefined;
  /** Absent for a reader — the create action is the page's to own and to gate. */
  onCreate?: () => void;
  /** A control that belongs to one view only, drawn after the shared filters. */
  extra?: ReactNode;
}

export function DealsToolbar({
  state,
  onStateChange,
  view,
  onViewChange,
  total,
  onCreate,
  extra,
}: DealsToolbarProps): ReactNode {
  return (
    <div className={styles.toolbar}>
      <Segmented
        label="Deals view"
        variant="inset"
        options={VIEW_OPTIONS}
        value={view}
        onChange={onViewChange}
      />

      <SearchInput
        value={state.q}
        onCommit={(q) => onStateChange({ ...state, q, page: 1 })}
        // Names the two columns the API actually searches (`searchColumns` on
        // the deals list spec). Offering partner search here would be a box
        // that quietly returns nothing for a partner everyone can see.
        placeholder="Search customer or notes"
        ariaLabel="Search deals"
      />

      {/* The partner's whole family, as the reports read it — see
          `DealsQueryState.partnerId`. */}
      <PartnerSelect
        value={state.partnerId}
        onChange={(partnerId) => onStateChange({ ...state, partnerId, page: 1 })}
      />

      <select
        className={styles.control}
        aria-label="Deal currency"
        value={state.currency ?? ANY_CURRENCY}
        onChange={(event) => {
          const picked = event.target.value;
          const currency = isCurrency(picked) ? picked : null;
          onStateChange({ ...state, currency, page: 1 });
        }}
      >
        <option value={ANY_CURRENCY}>Any currency</option>
        {Currency.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <select
        className={styles.control}
        aria-label="Filter by win probability"
        value={state.winProbMin ?? ANY_WIN}
        onChange={(event) => {
          const picked = Number(event.target.value);
          onStateChange({
            ...state,
            winProbMin: WIN_FLOORS.includes(picked) ? picked : null,
            page: 1,
          });
        }}
      >
        <option value={ANY_WIN}>Any win %</option>
        {WIN_FLOORS.map((floor) => (
          <option key={floor} value={floor}>
            {floor === 100 ? "Certain (100%)" : `At least ${floor}%`}
          </option>
        ))}
      </select>

      <select
        className={styles.control}
        aria-label="Start window"
        value={state.start}
        onChange={(event) => {
          const picked = START_WINDOWS.find((option) => option.value === event.target.value);
          onStateChange({ ...state, start: picked?.value ?? "any", page: 1 });
        }}
      >
        {START_WINDOWS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <select
        className={styles.control}
        aria-label="Renewal window"
        value={state.renewal}
        onChange={(event) => {
          const picked = RENEWAL_WINDOWS.find((option) => option.value === event.target.value);
          onStateChange({ ...state, renewal: picked?.value ?? "any", page: 1 });
        }}
      >
        {RENEWAL_WINDOWS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {dealsFiltersActive(state) && (
        <Button
          variant="subtle"
          small
          onClick={() =>
            onStateChange({
              ...state,
              q: "",
              states: [],
              partnerId: null,
              currency: null,
              winProbMin: null,
              start: "any",
              renewal: "any",
              country: null,
              location: "any",
              page: 1,
            })
          }
        >
          Clear filters
        </Button>
      )}

      <StateFilter
        selected={state.states}
        onChange={(states) => onStateChange({ ...state, states, page: 1 })}
      />

      {extra}

      <span className={styles.count}>
        {total === undefined ? "" : `${formatCount(total)} deals`}
      </span>

      {onCreate !== undefined && (
        <Button variant="primary" onClick={onCreate}>
          {/* The glyph is decoration, so the button's accessible name stays
              the words alone rather than "plus New deal". */}
          <span aria-hidden="true">+</span>
          New deal
        </Button>
      )}
    </div>
  );
}

/** A partner's whole family, as the reports read it, or every partner. */
export function PartnerSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (partnerId: string | null) => void;
}): ReactNode {
  const partners = usePartnerOptions();
  return (
    <select
      className={styles.control}
      aria-label="Filter by partner"
      value={value ?? ANY_PARTNER}
      onChange={(event) => {
        const picked = event.target.value;
        onChange(picked === ANY_PARTNER ? null : picked);
      }}
    >
      <option value={ANY_PARTNER}>Every partner</option>
      {(partners.data?.items ?? []).map((partner) => (
        <option key={partner.id} value={partner.id}>
          {partner.name}
        </option>
      ))}
    </select>
  );
}

function StateFilter({
  selected,
  onChange,
}: {
  selected: readonly DealState[];
  onChange: (next: DealState[]) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const label = selected.length === 0 ? "Any state" : selected.map(dealStateLabel).join(", ");

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: globalThis.MouseEvent): void => {
      if (event.target instanceof Node && root.current?.contains(event.target) === true) return;
      setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className={styles.filter} ref={root}>
      <button
        type="button"
        className={`${styles.control} ${styles.filterButton}`}
        aria-label={`Deal state: ${label}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((previous) => !previous)}
      >
        <span className={styles.filterValue}>{label}</span>
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className={styles.menu} role="group" aria-label="Deal state">
          {DealState.options.map((option) => (
            <label key={option} className={styles.menuItem}>
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={(event) => {
                  const next = event.target.checked
                    ? [...selected, option]
                    : selected.filter((value) => value !== option);
                  // Re-ordered by the enum, so the same selection is the same
                  // URL and the same query key however the boxes were ticked.
                  onChange(DealState.options.filter((value) => next.includes(value)));
                }}
              />
              {dealStateLabel(option)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function isCurrency(value: string): value is Currency {
  return Currency.safeParse(value).success;
}
